// Book desk modules, row 1: the summary tiles. Each face is fed by the SAME
// TanStack query keys/options as the panel behind its drawer (bookModuleHooks),
// so opening the drawer is a cache hit. Faces are state-only: no buttons or
// links inside.
//
// What a face must never do: read an unknown as a zero. A dormant book
// (`bookStatus === null`) is not queried and shows an explicit dormant rung; a
// query that is loading (or paused with no data) shows `—` and omits the compare
// line; one that failed with nothing to show gets the error rung. A halted
// book's reason is never hidden behind either (the header keeps its status line
// through loading and error).
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { accountApi, taxApi } from '../../api/client'
import { useNow } from '../../hooks/useNow'
import { etTodayISO } from '../../lib/cadence'
import { relativeTime } from '../../lib/relativeTime'
import type { Book } from '../../types'
import { PipStrip } from '../viz/PipStrip'
import { ProgressBar } from '../viz/ProgressBar'
import { Sparkline } from '../viz/Sparkline'
import { Module } from './Module'
import { StatModule } from './StatModule'
import {
  CLOCK_MS,
  ROLE_LABEL,
  useBookAccount,
  useBookDrawdown,
  useBookPositions,
  useSourceConviction,
  type BookModuleProps,
} from './bookModuleHooks'
import {
  fmtScore,
  liveGate,
  mirrorSummary,
  scoreCaveat,
  taxFace,
  valveSummary,
  type MirrorSummary,
  type ValveSummary,
} from './bookSummaries'
import {
  DASH,
  isDormant,
  num,
  POSITION_CAP,
  ROLE_INITIAL,
  signTone,
  stillLoading,
  unavailable,
} from './fleetModuleHooks'
import { drawdownFace, exposure, fmtPct, fmtSignedUsd, fmtUsd, worstDrawdown } from './fleetSummaries'
import { withMarker } from './statCompare'
import './BookModules.css'

type Tone = 'gain' | 'loss' | 'warn' | 'neutral'

/** Keeps a short phrase ("3m ago", "1 exit 30d") on one line when the compare line wraps. */
const together = (phrase: string) => phrase.replace(/ /g, '\u00a0')

// ------------------------------------------------------------------- header

/** The status line exactly as BookPage derives it: dormant, halted (with its reason), or running. */
function statusLine(book: Book): { text: string; tone: 'gain' | 'warn' | 'muted' } {
  const status = book.bookStatus
  if (status === null) return { text: 'dormant · not in engine', tone: 'muted' }
  if (status.halted === true) {
    return { text: status.halt_reason ? `halted: ${status.halt_reason}` : 'halted', tone: 'warn' }
  }
  return { text: 'running', tone: 'gain' }
}

/** One ellipsised line; the full text rides on the title. */
function BookStatusLine({ book }: BookModuleProps) {
  const { text, tone } = statusLine(book)
  return (
    <span className={`book-card-status book-card-status--${tone} book-header-status`} title={text}>
      <span className="book-card-status-dot" aria-hidden="true" />
      {text}
    </span>
  )
}

/**
 * The book's identity and state: label (the tile title), equity, day P&L, a 1M
 * equity sparkline, the role badge and the status / halt reason. Built on Module
 * directly because it needs this body.
 *
 * Module's error rung would replace the whole body, and a halted book's reason
 * must never disappear, so Module is never told about the error: it is drawn
 * inside the body, under the status line. Loading keeps the status line too.
 *
 * The badge is the role's initial with the full role as its accessible name: the
 * full word (Research 90px, Validation 97px at 12px uppercase) cannot share a
 * row with a 101px equity in the 172px body (BookModules.css).
 */
export function BookHeaderModule({ book }: BookModuleProps) {
  const dormant = isDormant(book)
  const accountQ = useBookAccount(book)
  const historyQ = useQuery({
    queryKey: ['equity-history', book.id, '1M'],
    queryFn: () => accountApi.history(book.id, '1M'),
    enabled: !dormant,
    retry: false,
    staleTime: 300000,
  })

  const title = book.label ?? `Account ${book.id}`
  const loading = !dormant && stillLoading(accountQ)
  const failed = !dormant && unavailable(accountQ)
  const account = accountQ.data
  const show = !dormant && !loading && account !== undefined

  if (failed) {
    return (
      <Module title={title} drawerKey="book-header" span={2} className="module--stat" isError={false}>
        <BookStatusLine book={book} />
        <p className="module-state">account unavailable</p>
      </Module>
    )
  }

  const equity = show ? num(account.account_value) : null
  const dailyPnl = show ? num(account.daily_pnl) : null
  const opening = equity !== null && dailyPnl !== null ? equity - dailyPnl : null
  const pct = dailyPnl !== null && opening !== null && opening > 0 ? (dailyPnl / opening) * 100 : null
  const pnlText = !show
    ? null
    : dailyPnl === null
      ? `${DASH} today`
      : `${fmtSignedUsd(dailyPnl)} · ${pct === null ? DASH : fmtPct(pct)} today`

  const series = show
    ? (historyQ.data?.points ?? []).map((p) => num(p.equity)).filter((v): v is number => v !== null)
    : []
  // As BookSparkline: gain when the window ends at or above where it began.
  const sparkColor = series.length >= 2 && series[series.length - 1] < series[0] ? 'var(--loss)' : 'var(--gain)'

  return (
    <Module title={title} drawerKey="book-header" span={2} className="module--stat" isError={false}>
      <div className="book-header">
        <div className="stat-value num" aria-busy={loading || undefined}>
          {equity === null ? DASH : fmtUsd(equity)}
        </div>
        <Sparkline values={series} color={sparkColor} height={28} ariaLabel={`${title} equity 1M`} />
        <div className="book-header-day">
          {pnlText !== null && <div className="stat-compare caption">{pnlText}</div>}
          <span
            className={`book-card-role-badge book-card-role-badge--${book.role}`}
            role="img"
            aria-label={ROLE_LABEL[book.role]}
          >
            {ROLE_INITIAL[book.role]}
          </span>
        </div>
        <BookStatusLine book={book} />
      </div>
    </Module>
  )
}

// ------------------------------------------------------------ mirror + gate

const MIRROR_FACE: Record<MirrorSummary['state'], { value: string; tone: Tone }> = {
  synced: { value: 'SYNCED', tone: 'gain' },
  drift: { value: 'DRIFT', tone: 'warn' },
  failed: { value: 'FAILED', tone: 'loss' },
  awaiting: { value: 'AWAITING', tone: 'neutral' },
  'no-source': { value: DASH, tone: 'neutral' },
}

/** The live-gate tile's HALTED convention (GATE_FACE), for a halted book that has no mirror tick. */
const MIRROR_HALTED_FACE: { value: string; tone: Tone } = { value: 'HALTED', tone: 'warn' }

/**
 * Validation row 1: is the subscriber book keeping step with its pick source?
 * From the engine's in-memory mirror tick on the book status, so there is no
 * query. A book that is not in the engine has no tick to wait for, so it shows a
 * dormant rung rather than "awaiting". The role badge is on the header tile, so
 * there is no caption here.
 *
 * The compare line prints the tick's age, and the tick lives on the book status:
 * nothing else renders this tile between status polls, so it re-reads the clock
 * (useNow, as the valve tile does) or "3m ago" would stand still on an idle tab.
 */
export function MirrorFidelityModule({ book }: BookModuleProps) {
  useNow(CLOCK_MS)
  const s = mirrorSummary(book)
  // A halted book has no tick by construction: "awaiting" would tell the operator to wait for one that will not come.
  const halted = s.state === 'awaiting' && book.bookStatus?.halted === true
  const face = halted ? MIRROR_HALTED_FACE : MIRROR_FACE[s.state]
  const total = s.synced + s.pendingEntry + s.pendingExit
  const stepping = s.state === 'synced' || s.state === 'drift'

  let compare: string
  if (stepping) {
    compare = `${s.synced} synced · ${s.pendingEntry} in · ${s.pendingExit} out · ${together(relativeTime(s.asOf))}`
  } else if (s.state === 'failed') {
    compare = 'last mirror tick failed'
  } else if (halted) {
    compare = 'book halted · no mirror tick'
  } else if (s.state === 'awaiting') {
    compare = 'awaiting first mirror tick since restart'
  } else {
    compare = 'no pick source configured'
  }

  return (
    <StatModule
      title="Mirror"
      drawerKey="mirror-fidelity"
      value={face.value}
      tone={face.tone}
      isEmpty={isDormant(book)}
      emptyText="dormant · not in engine"
      compare={compare}
      footer={
        stepping && total > 0 ? (
          <ProgressBar value={s.synced} max={total} tone="gain" ariaLabel={`${s.synced} of ${total} names synced`} />
        ) : undefined
      }
    />
  )
}

const GATE_FACE: Record<'dormant' | 'halted' | 'running', { value: string; tone: Tone }> = {
  dormant: { value: 'DORMANT', tone: 'neutral' },
  halted: { value: 'HALTED', tone: 'warn' },
  running: { value: 'RUNNING', tone: 'gain' },
}

/**
 * Live row 1: where the live book stands against its three gates (an enabled
 * registry row, a feed subscription, a book in the engine). From the book
 * status alone, so there is no query.
 */
export function LiveGateModule({ book }: BookModuleProps) {
  const g = liveGate(book)
  const face = GATE_FACE[g.state]
  const { registry, feed, engine } = g.gates

  let compare: ReactNode
  if (g.state === 'dormant') compare = 'built-but-OFF · not an engine book'
  // The engine's reason has no length limit; the tile shows two lines of it
  // (BookModules.css) and the title carries the rest.
  else if (g.state === 'halted') compare = g.reason ? <span title={g.reason}>{g.reason}</span> : 'halted (no reason reported)'
  else compare = 'env gate + feed subscription'

  return (
    <StatModule
      title="Live gate"
      drawerKey="live-gate"
      value={face.value}
      tone={face.tone}
      compare={compare}
      footer={
        <PipStrip
          // A closed gate is `off` (visible), not `empty` (a near-background placeholder): on a dormant
          // book the closed gates are the point, and the three-gate encoding must survive.
          pips={[registry, feed, engine].map((ok): 'ok' | 'off' => (ok ? 'ok' : 'off'))}
          ariaLabel={`gates: registry ${registry ? 'enabled' : 'disabled'}, feed ${feed ? 'subscribed' : 'none'}, engine book ${engine ? 'built' : 'not built'}`}
        />
      }
    />
  )
}

// ----------------------------------------------------------------- drawdown

/** This book's drawdown against its floor: the Fleet tile's face for one book. */
export function BookDrawdownModule({ book }: BookModuleProps) {
  const dormant = isDormant(book)
  const q = useBookDrawdown(book)

  const loading = !dormant && stillLoading(q)
  const failed = !dormant && unavailable(q)
  const face = drawdownFace(worstDrawdown([{ status: q.data, label: book.role }]), failed ? 1 : 0)

  return (
    <StatModule
      title="Drawdown floor"
      drawerKey="drawdown-floor"
      value={face.value}
      tone={face.tone}
      isLoading={loading}
      isError={failed}
      errorText="drawdown unavailable"
      isEmpty={dormant}
      emptyText="dormant · no floor"
      compare={loading ? undefined : withMarker(face.base, face.marker)}
      footer={face.bar ? <ProgressBar {...face.bar} /> : undefined}
    />
  )
}

// ----------------------------------------------------------------- exposure

/**
 * Gross exposure over equity, cash, and the broker's held count. The count is
 * the broker's positions (ruling 28): the conviction `held` is zero for a
 * subscriber book. The drawdown-floor read (a cache hit) only supplies the
 * exposure-scaling target (ruling 27).
 */
export function BookExposureModule({ book }: BookModuleProps) {
  const dormant = isDormant(book)
  const positionsQ = useBookPositions(book)
  const accountQ = useBookAccount(book)
  const ddQ = useBookDrawdown(book)

  const loading = !dormant && (stillLoading(positionsQ) || stillLoading(accountQ))
  const failed = !dormant && (unavailable(positionsQ) || unavailable(accountQ))
  const { ratio, cash } = exposure(positionsQ.data, accountQ.data)
  const held = positionsQ.data === undefined ? DASH : String(positionsQ.data.length)
  const scaling = ddQ.data?.exposure
  const target = scaling?.enabled ? num(scaling.target) : null
  const scalingText = target === null ? '' : ` · scaling target ${Math.round(target * 100)}%`

  return (
    <StatModule
      title="Exposure"
      drawerKey="exposure"
      value={ratio === null ? DASH : `${ratio.toFixed(2)}×`}
      isLoading={loading}
      isError={failed}
      errorText="exposure unavailable"
      isEmpty={dormant}
      emptyText="dormant"
      compare={
        loading ? undefined : `cash ${cash === null ? DASH : fmtUsd(cash)} · ${held}/${POSITION_CAP} held${scalingText}`
      }
      footer={
        ratio === null ? undefined : (
          <ProgressBar value={ratio} max={1} ariaLabel={`gross exposure ${ratio.toFixed(2)}×`} />
        )
      }
    />
  )
}

// --------------------------------------------------------------- exit valve

/** A score this close under θ is worth a warning; at or over it the valve fires. */
const VALVE_WARN_BAND = 0.05

/**
 * The ONE warn marker the valve compare may carry, most important first: the
 * scores are old (the engine stopped writing the log, say), the book's holdings
 * are unknown (the scorecard did not load, so nothing says whether the scored
 * names are all of them), or fewer names were scored than are held. One marker
 * keeps the compare within its two lines. The scores themselves are always
 * still shown: they are real, only old or unqualified.
 */
function valveMarker(
  v: ValveSummary,
  today: string,
): { kind: 'stale' | 'coverage' | 'partial'; text: string } | null {
  // The precedence and the stale bar are bookSummaries' (the overlay's θ row applies the same ones).
  const caveat = scoreCaveat(v, today)
  if (caveat === null) return null
  if (caveat.kind === 'stale') return { kind: 'stale', text: `· ${together(`as of ${caveat.logDate.slice(5)}`)}` }
  if (caveat.kind === 'coverage') return { kind: 'coverage', text: `· ${together('coverage unknown')}` }
  return { kind: 'partial', text: `· ${together(`${caveat.names}/${caveat.held} scored`)}` }
}

/**
 * The thesis-break valve (ruling 24): the highest score of the latest confidence
 * day against θ, since the valve fires when a name's score reaches θ. A
 * subscriber book has no conviction rows, so every non-research book shows the
 * RESEARCH book's valve and the title says so (rulings 26, 38); the title follows
 * where the figures come from (a pick source), not the role, so a book with no
 * source reads its own data and is just the "Exit valve". On an exit day
 * the top name is the one that just exited (the log is written before the
 * tick's exits), hence "over θ", not "nearest". No score is ever shown rounded
 * up to θ (fmtScore truncates), and an empty or failed log is never a 0.00.
 */
export function ExitValveModule({ book }: BookModuleProps) {
  const { statusQ, scorecardQ, logQ, sourceless } = useSourceConviction(book)
  const title = book.pick_source_account_id != null ? 'Research valve' : 'Exit valve'

  const loading = !sourceless && [statusQ, scorecardQ, logQ].some(stillLoading)
  // Re-read the clock: a log that stops being written is the same rows on every refetch, so only
  // the time can make the tile say the scores have become old.
  const now = useNow(CLOCK_MS)
  const today = etTodayISO(now)
  const v = valveSummary(statusQ.data, scorecardQ.data, logQ.data, today)
  const { theta, top } = v
  const over = top !== null && theta !== null && top.score >= theta
  const near = top !== null && theta !== null && top.score >= theta - VALVE_WARN_BAND

  let tone: Tone = over ? 'loss' : near ? 'warn' : 'neutral'
  let compare: ReactNode
  const thetaText = theta === null ? DASH : theta.toFixed(2)
  if (top === null) {
    // A log that failed to load is not a log with no scores.
    const failedScores = unavailable(logQ)
    if (failedScores) tone = 'warn'
    compare = `θ ${thetaText} · ${failedScores ? 'scores unavailable' : 'no scores yet'}`
  } else {
    const exits = v.exits30d === null ? DASH : String(v.exits30d)
    const symbol = top.symbol === '' ? DASH : top.symbol
    const marker = valveMarker(v, today)
    // No scorecard means no exit count either, and "coverage unknown" already says so: the
    // marker takes the count's place. Keeping the dash too leaves `— exits 30d · coverage
    // unknown` as one 192px unit (177px even at 12px) in a 172px body: a third line, which
    // pushes the bar out. Behind a stale marker the dash stays: it is then the only sign of
    // the gap.
    const exitsPart = marker?.kind === 'coverage'
      ? null
      : together(`${exits} ${v.exits30d === 1 ? 'exit' : 'exits'} 30d`)
    const base = [`θ ${thetaText}`, `${symbol} ${over ? 'over θ' : 'nearest'}`, exitsPart]
      .filter((part) => part !== null)
      .join(' · ')
    compare = withMarker(base, marker?.text ?? null)
  }

  return (
    <StatModule
      title={title}
      drawerKey="exit-valve"
      value={top === null ? DASH : fmtScore(top.score)}
      tone={tone}
      isLoading={loading}
      // θ is the anchor: without the status there is no valve to read.
      isError={!sourceless && unavailable(statusQ)}
      errorText="valve unavailable"
      isEmpty={sourceless}
      emptyText="dormant · no pick source"
      compare={loading ? undefined : compare}
      footer={
        top !== null && theta !== null ? (
          <ProgressBar
            value={top.score}
            max={1}
            marker={theta}
            tone={tone === 'neutral' ? 'brand' : tone}
            ariaLabel={`highest score ${fmtScore(top.score)} against θ ${theta.toFixed(2)}`}
          />
        ) : undefined
      }
    />
  )
}

// ---------------------------------------------------------------------- tax

const signed = (v: number | null) => (v === null ? DASH : fmtSignedUsd(v))

/**
 * Realized YTD (ruling 32): the ECONOMIC realized figure leads, since that is the
 * book's real gain or loss; the reportable figure (after wash-sale deferrals) and
 * the deferral still parked in basis ride on the compare line. Same key and
 * options as TaxSummaryCard.
 */
export function TaxModule({ book }: BookModuleProps) {
  const dormant = isDormant(book)
  const year = new Date().getFullYear()
  const q = useQuery({
    queryKey: ['tax-summary', year, book.id],
    queryFn: () => taxApi.getSummary(year, book.id),
    enabled: !dormant,
    retry: 1,
    refetchInterval: 60000,
  })

  const loading = !dormant && stillLoading(q)
  const face = taxFace(q.data)
  // The API answered, but the ledger has not materialized a summary yet.
  const notMaterialized = q.data !== undefined && !face.available

  return (
    <StatModule
      title="Realized YTD"
      drawerKey="tax"
      value={signed(face.economic)}
      tone={face.economic === null ? 'neutral' : signTone(face.economic)}
      isLoading={loading}
      isError={!dormant && unavailable(q)}
      errorText="tax unavailable"
      isEmpty={dormant || notMaterialized}
      emptyText={
        dormant ? 'dormant · no ledger' : 'no tax summary yet — materializes after the first ledger reconciliation'
      }
      compare={
        loading
          ? undefined
          : `reportable ${signed(face.reportable)} · deferred ${signed(face.deferredNet)} · ${together(`${face.washCount} wash`)}`
      }
    />
  )
}
